import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Hook personnalisé pour utiliser le contexte de langue
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Détecter la langue du navigateur et choisir une langue supportée
const getBrowserLanguage = (supportedLanguages) => {
  // Obtenir la langue préférée du navigateur
  const browserLang = navigator.language || navigator.languages[0];
  // Extraire le code de langue (ex: 'fr-FR' -> 'fr')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Vérifier si la langue est supportée, sinon fallback vers l'anglais
  return supportedLanguages.includes(langCode) ? langCode : 'en';
};

export const LanguageProvider = ({ children, messages }) => {
  const supportedLanguages = Object.keys(messages);
  
  // Initialiser la langue : localStorage > langue navigateur > anglais
  const getInitialLanguage = () => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
      return savedLanguage;
    }
    return getBrowserLanguage(supportedLanguages);
  };

  const [language, setLanguageState] = useState(getInitialLanguage);

  // Fonction pour changer la langue
  const setLanguage = (newLanguage) => {
    if (supportedLanguages.includes(newLanguage)) {
      setLanguageState(newLanguage);
      localStorage.setItem('preferredLanguage', newLanguage);
    }
  };

  // Effet pour écouter les changements de langue du navigateur
  useEffect(() => {
    const handleLanguageChange = () => {
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (!savedLanguage) {
        // Si aucune langue n'est sauvegardée, utiliser celle du navigateur
        const newLanguage = getBrowserLanguage(supportedLanguages);
        setLanguageState(newLanguage);
      }
    };

    // Écouter les changements de langue du navigateur (rare mais possible)
    window.addEventListener('languagechange', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }, [supportedLanguages]);

  const value = {
    language,
    setLanguage,
    supportedLanguages,
    currentMessages: messages[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};